import { ApiError, isRequestTimeout } from "@/lib/api/client";
import type { HaltHistoryItem, Package } from "../api/deployments.api";

const RELEASE_HALTED_CODE = "release_halted";

export function isHaltHistoryUnsupported(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.rawError === "Route not found";
}

export function isReleaseHalted(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === RELEASE_HALTED_CODE;
}

export function shouldShowHaltHistoryPanel(access: {
  serverCapabilities: boolean;
  can: (capability: "app.read") => boolean;
}): boolean {
  return !(access.serverCapabilities && !access.can("app.read"));
}

export function canRecomputeHaltGate(item: HaltHistoryItem): boolean {
  return (
    item.servedDevices !== null &&
    item.failedDevices !== null &&
    item.thresholdFailureRate !== null &&
    item.thresholdMinFailures !== null &&
    item.thresholdMinReports !== null
  );
}

export function shouldRenderHaltHistoryPanel(
  access: { serverCapabilities: boolean; can: (capability: "app.read") => boolean },
  error: unknown,
  isPending: boolean,
  hasItems = false,
): boolean {
  if (isPending && !hasItems) return false;
  if (!shouldShowHaltHistoryPanel(access)) return false;
  if (isHaltHistoryUnsupported(error) && !hasItems) return false;
  return true;
}

export function haltHistoryRetry(failureCount: number, error: ApiError): boolean {
  if (isHaltHistoryUnsupported(error)) return false;
  if (isRequestTimeout(error)) return false;
  if (error.status === 401 || error.status === 403 || error.status === 404) return false;
  if (error.status === 429) return false;
  return failureCount < 2;
}

export function haltHistoryRefetchInterval(error: unknown): number | false {
  return isHaltHistoryUnsupported(error) ? 60_000 : false;
}

export function haltOverrideStamp(item: HaltHistoryItem): string | null {
  if (!item.overriddenAt) return null;
  if (item.overriddenAt < item.occurredAt) return null;
  return item.overriddenAt;
}

export function isAutoHalted(
  release: Pick<Package, "label" | "isDisabled">,
  items: readonly HaltHistoryItem[] | undefined,
): boolean {
  if (release.isDisabled !== true || !items) return false;
  let newestHalt: HaltHistoryItem | undefined;
  for (const item of items) {
    if (item.kind !== "halt" || item.label !== release.label) continue;
    if (!newestHalt || item.occurredAt > newestHalt.occurredAt) newestHalt = item;
  }
  return newestHalt !== undefined && haltOverrideStamp(newestHalt) === null;
}
